import React from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertCircle, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';

const TimeCritical = () => {
  const features = [
    'Immediate AOG response team activation',
    'Next-flight-out (NFO) arrangements',
    'Hand-carry and onboard courier options',
    'Private aircraft charter coordination',
    'Real-time shipment tracking',
    'Dedicated project management',
    'Customs pre-clearance',
    '24/7/365 operational support',
  ];

  const industries = [
    { name: 'Commercial Aviation', description: 'Critical spares for grounded aircraft' },
    { name: 'Defence & Aerospace', description: 'Classified and high-value components' },
    { name: 'Manufacturing', description: 'Production line emergency parts' },
    { name: 'Oil & Gas', description: 'Offshore platform urgent equipment' },
  ];

  const steps = [
    { step: '01', title: 'Immediate Contact', time: '<5 minutes' },
    { step: '02', title: 'Route Planning', time: '<30 minutes' },
    { step: '03', title: 'Collection Arranged', time: '<2 hours' },
    { step: '04', title: 'In Transit', time: 'Next flight out' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Time-Critical & AOG Logistics | Aero Time Critical" description="24/7 AOG response, next-flight-out, onboard courier and aircraft charter for urgent aircraft parts and aerospace components." path="/time-critical" />
      <PageHero
        badge="TIME-CRITICAL SOLUTIONS"
        BadgeIcon={Clock}
        title="Time-Critical &"
        titleAccent="AOG Services"
        description="When aircraft are grounded or production lines are stopped, every minute counts. Our specialist AOG team delivers critical components with unmatched speed and reliability."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/esv8b4gm_AOG%20ATC%20.png"
      />

      {/* Process Steps */}
      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">Our Process</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 text-[#003057]">How We Respond</h2>
            <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
              Our proven AOG process ensures your critical shipment moves faster than you thought possible
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg border border-[#E5E9EF] text-center"
              >
                <div className="text-5xl font-bold text-[#0089D0]/20 mb-3">{item.step}</div>
                <h3 className="text-xl font-bold mb-2 text-[#003057]">{item.title}</h3>
                <div className="text-[#C9A227] text-lg font-semibold">{item.time}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#003057]">
                Complete AOG Capability
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="text-[#0089D0] flex-shrink-0 mt-1" size={22} />
                    <span className="text-[#003057] font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-[#003057] text-white p-8 rounded-lg">
                <AlertCircle size={44} className="mb-4 text-[#C9A227]" />
                <h3 className="text-2xl font-bold mb-3 text-white">AOG Emergency Line</h3>
                <p className="text-gray-300 mb-6">Direct access to our AOG specialists 24/7/365</p>
                <a href="tel:+441753720055">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    className="btn-primary w-full"
                  >
                    Call Now: +44 (0) 1753 720055
                  </motion.button>
                </a>
              </div>

              <div className="border border-[#E5E9EF] p-8 rounded-lg bg-[#F7F9FB]">
                <h3 className="text-xl font-bold mb-4 text-[#003057]">Industries We Serve</h3>
                <div className="space-y-4">
                  {industries.map((industry, index) => (
                    <div key={index} className="border-l-3 border-l-4 border-[#0089D0] pl-4">
                      <h4 className="font-semibold text-[#003057] mb-1">{industry.name}</h4>
                      <p className="text-[#4A4A4A] text-sm">{industry.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection
        title="Every Second Matters"
        description="Contact our AOG team now for immediate assistance with your time-critical shipment"
        buttonText="Request Urgent Quote"
      />
    </div>
  );
};

export default TimeCritical;
